import * as React from "react";
import { createPortal } from "react-dom";
import { CloseOutlined } from "@ant-design/icons";
import * as TreeRepository from '../../../Repository/TreeRepository';
import CarouselItem from "./CarouselItem";
import TreeDescription from "./TreeDescription";
import message from "antd/es/message";
import { Carousel, Empty, Modal, Pagination, Row, Col } from "antd";
import { useEffect, useRef, useState } from "react";
import { Contract, Tree, User } from "tree-wallet-client";
import { UserRoles } from "../../../Repository/UserRepository";
import { isAllowed } from "../../../Service/AuthService";
import MapPopupErrorBoundary from "./MapPopupErrorBoundary";
import MapPopupValidateButton from "./MapPopupValidateButton";
import MapPopupDeleteButton from "./MapPopupDeleteButton";
import { collapseModalAtPoint } from "../../../Helper/PopupCollapse";
import ValidationTable from "../../../Component/ValidationTable/ValidationTable";
import { isDesktopLayout } from "../../../Helper/DeviceHelper";
import { Spin } from "antd/es";
import PhotoViewer from "../../../Component/PhotoViewer";
import { readTreesListCopyTree } from "../../../Dependency/NativeAppMemory";
import { TREE_NOT_FOUND } from '../../../Helper/BannerMessages';

// ---------------------------------------------------------------------------------------------------
// Tree popup configuration: the window settings can all be edited here
// ---------------------------------------------------------------------------------------------------

// ================== MOBILE ==================
// Air between the window and the left/right edge of the screen
const POPUP_MOBILE_LEFT_PADDING = 10;
const POPUP_MOBILE_RIGHT_PADDING = 10;
// Width the window would take when the screen has room to spare: the narrow popup always wins
const POPUP_MOBILE_DEFAULT_WIDTH = 600;

// ================== DESKTOP ==================
// Air between the window and the left edge of the screen - the width is the trees list minus that
// same air, so the window stops on the map's left edge
const POPUP_DESKTOP_LEFT_PADDING = 0;
// The two columns of the body: the details on the left, the photo on the right
const POPUP_DESKTOP_COLUMNS = ['50%', '50%'];
const POPUP_DESKTOP_GUTTER = 24;
// Air above the Validations row, which gets a line of its own under the details and the photo
const POPUP_DESKTOP_VALIDATIONS_PADDING_TOP = 10;

// ================== BOTH ==================
// Air between the window and the top edge of the screen
const POPUP_TOP_PADDING = 10;

// Vertical air between the body's rows: photo/details, then the validations
const POPUP_SECTION_GAP = 16;

// Space between the two actions of the window, Delete and Validate
const POPUP_ACTION_GAP = 16;

// How far the photo carousel tabs are drawn above the photo
const POPUP_PAGINATION_LIFT = -10;

/**
 * Resolves once the photo can be painted. The tree data comes back well before the picture has been
 * fetched and decoded, and showing the window at that moment let the photo appear progressively,
 *  right after the spinner had collapsed
 */
function waitForPhoto(url?: string): Promise<void> {
  return new Promise((resolve) => {
    if (!url) {
      resolve();
      return;
    }

    const image = new Image();
    const done = () => {
      // `decode` is what separates "downloaded" from "ready to paint"
      if (image.decode) {
        image.decode().then(() => resolve(), () => resolve());
      } else {
        resolve();
      }
    };
    image.onload = done;
    // A photo that cannot be loaded is no reason to keep the window loading for ever
    image.onerror = () => resolve();
    image.src = url;
  });
}

interface MapPopupProps {
  language: string;
  contracts?: Contract[];
  reloadTrees: Function;
  updateState: Function;
  user: User;
  treeId: number;
  onClose?: () => void;
  onCloseStart?: () => void;
  // Where this tree's icon sits on screen: the closing animation is directed at it.
  getMarkerScreenPoint?: () => { x: number; y: number } | null;
}

export interface ValidatedTree {
  id: string;
  type: string;
  number: string;
  validationData?: any;
}

export default function MapPopup(props: MapPopupProps) {
  const { treeId, language, reloadTrees, user, updateState, contracts, onClose } = props;
  // Whether the trees list is beside the map (see `isDesktopLayout`): it is what the window's own body
  // layout follows, so a tablet displayes the same two columns as on desktop
  const desktopLayout = isDesktopLayout();

  const [isValidated, setIsValidated] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(true);

  // Set when the window is closing right after its validation was written to the queue: it then
  // collapses towards the queue button the tree landed in, instead of towards the tree's icon.
  const queuedValidationRef = useRef(false);

  // Screen centre of the validator's queue button. Looked up in the DOM because the queue lives in
  // its own component and all that is needed here is a position - the same approach as the planter
  // form's `getQueueScreenPoint`.
  const getQueueScreenPoint = () => {
    const button = document.querySelector('.btn-open-validated-queue');
    if (!button) {
      return null;
    }

    const rect = button.getBoundingClientRect();
    // The queue is not shown at every screen size: a button with no box has no position to aim at.
    if (!rect.width || !rect.height) {
      return null;
    }

    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  const initiateClose = () => {
    // The button goes first: it is fixed to the screen, so letting it ride the window's closing zoom
    // would drag it across the map before it vanished
    setClosing(true);
    props.onCloseStart?.();
    // The window collapses into its own tree icon instead of into the middle of the screen - or, when
    // the validation has just been saved, towards the queue button it was written to.
    const collapsePoint = (queuedValidationRef.current ? getQueueScreenPoint() : null)
      ?? props.getMarkerScreenPoint?.()
      ?? null;
    collapseModalAtPoint(collapseAnchorRef.current, collapsePoint);
    setOpen(false);
  };
  const [tree, setTree] = useState<Tree | undefined>(undefined);
  // Set while the window is closing: the close button is hidden from that moment (see `initiateClose`)
  const [closing, setClosing] = useState<boolean>(false);
  const [contract, setContract] = useState<Contract | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false); // For delete button
  const [pageLoading, setPageLoading] = useState<boolean>(true); // For initial load
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);
  const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
  const [validationInProgress, setValidationInProgress] = useState<boolean>(false); // Track validation state
  // Photo shown by the full screen viewer; empty means the viewer is closed.
  const [viewerImageUrl, setViewerImageUrl] = useState<string | undefined>(undefined);
  // When delete confirmation popup is open, Validate button is disabled
  // (the delete button reports it, see `MapPopupDeleteButton`)
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);
  const carouselRef = useRef<any>(null);
  // Any node inside the window is enough for the closing collapse, see `collapseModalAtPoint`.
  const collapseAnchorRef = useRef<HTMLSpanElement>(null);

  // On Desktop display the tree popupo over the trees list / on Mobile centered
  const [windowWidth, setWindowWidth] = useState<number | undefined>(undefined);
  
  // The window's right edge, where the close button sits half over the map on Desktop, the map's left edge on
  // on Mobile the right edge of the tree popup displaying partly the close button outside the screen
  // CSS cannot work it out from the flex layout
  const [popupSeam, setPopupSeam] = useState<number | undefined>(undefined);
  
  // Mobile: tree popup centered
  const [centered, setCentered] = useState(false);


  useEffect(() => {
    const measure = () => {
      // The trees list & the map are adjacent: the trees list's right edge is the map's left edge
      const drawnMap = Array.from(document.querySelectorAll('.mapboxgl-map'))
        .find((map) => map.getBoundingClientRect().width > 0);
      const listRightEdge = drawnMap ? drawnMap.getBoundingClientRect().left : 0;
      const desktop = listRightEdge > 200;

      // The window fills the trees list and stops on the map's left edge: it never covers the map
      setWindowWidth(desktop ? listRightEdge - POPUP_DESKTOP_LEFT_PADDING : undefined);

      const width = desktop
        ? listRightEdge - POPUP_DESKTOP_LEFT_PADDING
        : Math.min(POPUP_MOBILE_DEFAULT_WIDTH, window.innerWidth - POPUP_MOBILE_LEFT_PADDING - POPUP_MOBILE_RIGHT_PADDING);

      const landscape = !desktop && window.innerWidth > window.innerHeight;
      setCentered(landscape);

      setPopupSeam(landscape
        ? (window.innerWidth + width) / 2
        : desktop
          ? POPUP_DESKTOP_LEFT_PADDING + width
          : POPUP_MOBILE_LEFT_PADDING + width);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Fetch tree data
  const fetchTreeData = async () => {
    setPageLoading(true);
    setPreviewImage(undefined); // Clear any existing preview when switching trees
    try {
      // The trees list copy first, the API otherwise
      let treeData: Tree | null = await readTreesListCopyTree(Number(treeId));

      if (!treeData) {
        try {
          // `Http` reports a failed read and resolves with nothing, so an empty answer is a failure too
          treeData = (await TreeRepository.getTree({ params: { treeId } })) || null;
        } catch (error) {
          treeData = null;
        }
      }

      if (!treeData) {
        // Neither the trees list copy nor the API answered: the log names the failed tree
        console.warn('no data for this tree, the trees list copy and the API both answered nothing', { treeId });
        message.error(TREE_NOT_FOUND);
        return;
      }

      setTree(treeData);
      const contractData = contracts?.find((c) => Number(c.id) === Number(treeData!.contractId));
      setContract(contractData);
      setIsValidated(treeData.validationHash !== null);

      // Default to the newest photo (last position due to oldest-first sorting)
      const photoCount = treeData.photos?.length || 0;
      if (photoCount > 0) {
        // If there's a preview image, newest photo is at index photoCount, otherwise photoCount - 1
        const defaultIndex = previewImage ? photoCount : photoCount - 1;
        setCurrentPhotoIndex(defaultIndex);
      }

      // The spinner stays until that photo is painted, not until the data is in
      const newestPhoto = [...(treeData.photos ?? [])]
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .pop();
      await waitForPhoto(newestPhoto?.imageUrl);
    } catch (err) {
      console.error(err);
      message.error(TREE_NOT_FOUND);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchTreeData();
  }, [treeId, contracts]);

  const isAdmin = isAllowed(user.roles, [UserRoles.Admin]);
  // const isValidator = isAllowed(contract?.roles, [ContractRepository.roles.Validator]);

  // Sort photos by creation date (oldest first, newest last)
  const sortedPhotos = tree?.photos
    ? [...tree.photos].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    : [];

  const onPageChange = (page: number) => {
    setCurrentPhotoIndex(page - 1);
    carouselRef.current?.goTo(page - 1);
  };

  const handlePhotoCapture = (url?: string) => {
    setPreviewImage(url);
    if (url) {
      setCurrentPhotoIndex(0);
      carouselRef.current?.goTo(0);
    }
  };

  const openViewer = (url: string) => {
    setViewerImageUrl(url);
  };

  const totalItems = (sortedPhotos?.length || 0) + (previewImage ? 1 : 0);

  // The photo counter, centred over the picture and 10px higher than its own slot
  const paginationBlock = totalItems > 1
    ? <div style={{ display: 'flex', justifyContent: 'center', transform: `translateY(${POPUP_PAGINATION_LIFT}px)` }}>
      <Pagination
        onChange={onPageChange}
        current={currentPhotoIndex + 1}
        total={totalItems}
        pageSize={1}
        size="small"
      />
    </div>
    : null;

  const photoBlock = (
    <>
      {totalItems === 0 && <Empty description={'No image'} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {totalItems === 1 && (
        <div className="map-tree description">
          {previewImage && (
            <CarouselItem
              key="preview"
              imageUrl={previewImage}
              isActive={true}
              onClick={openViewer}
            />
          )}
          {!previewImage && sortedPhotos && sortedPhotos.length > 0 && (
            <CarouselItem
              key={sortedPhotos[0].id}
              imageUrl={sortedPhotos[0]?.imageUrl}
              isActive={true}
              onClick={openViewer}
            />
          )}
        </div>
      )}
      {totalItems > 1 && (
        <Carousel
          ref={carouselRef}
          afterChange={(index) => {
            setCurrentPhotoIndex(index);
          }}
          initialSlide={currentPhotoIndex}
          arrows={true}
          // A dot per photo, as a row of its own under the picture (see map.less)
          dots={true}
          swipeToSlide={true}
          draggable={true}
          touchMove={true}
          infinite={false}
          speed={300}
          easing="ease-out"
          style={{ touchAction: 'auto' }}
        >
          {previewImage && (
            <CarouselItem
              key="preview"
              imageUrl={previewImage}
              isActive={0 === currentPhotoIndex}
              onClick={openViewer}
            />
          )}
          {sortedPhotos && sortedPhotos.map((p, index) => {
            return (
              <CarouselItem
                key={p.id}
                imageUrl={p?.imageUrl}
                isActive={index === currentPhotoIndex}
                onClick={openViewer}
              />
            );
          })}
        </Carousel>
      )}
    </>
  );

  const descriptionBlock = tree && (
    <TreeDescription
      tree={tree}
      user={user}
      language={language}
      isValidated={isValidated}
      contract={contract}
    />
  );

  // The tree's validations, when it has any: a row of their own under the details and the photo
  const validationsBlock = tree && tree.treeValidations && tree.treeValidations.length > 0
    ? <ValidationTable tree={tree} />
    : null;

  // Delete & Validate, on the left edge on Mobile; on Desktop they close the details column
  const actionsBlock = contract && tree && (
    <Row justify="start" align="middle" gutter={POPUP_ACTION_GAP}>
      <Col>
        <MapPopupDeleteButton
          tree={tree}
          user={user}
          isAdmin={isAdmin}
          isValidated={isValidated}
          loading={loading}
          contract={contract}
          setLoading={setLoading}
          setOpenedPopup={initiateClose}
          reloadTrees={reloadTrees}
          validationInProgress={validationInProgress}
          onBusyChange={setDeleteBusy}
        />
      </Col>
      <Col>
        <MapPopupValidateButton
          setIsValidated={setIsValidated}
          isAdmin={isAdmin}
          language={language}
          tree={tree}
          user={user}
          contract={contract}
          updateState={updateState}
          deleteBusy={deleteBusy}
          onPhotoCapture={handlePhotoCapture}
          setValidationInProgress={setValidationInProgress}
          onValidationSuccess={() => {
            // The validation really is in the queue by now (the form only reports
            // success once the write has answered): it is what the window
            // collapses towards as it closes.
            queuedValidationRef.current = true;
            fetchTreeData();
            initiateClose();
          }}
        />
      </Col>
    </Row>
  );

  // Check if a tree popup is empty (opened offline on a tree whose data was never copied): 
  // the window holds a single "Tree data could not be loaded" message
  const emptyCentered = !windowWidth && !pageLoading && !tree;

  return (
    <>
      <Modal
        open={open}
        maskClosable={false}
        title={''}
        // The close button is the page's own, rendered outside the window (see the end of this component)
        closable={false}
        // The close button is drawn like the PhotoViewer's one (see `.tree-popup .ant-modal-close`)
        className={'tree-popup' + (closing ? ' tree-popup-closing' : '')}
        width={windowWidth ?? POPUP_MOBILE_DEFAULT_WIDTH}
        // on Mobile an empty tree popup is displayed at the center of the screen (see `emptyCentered`)
        centered={emptyCentered}
        // On Mobile the window opens 10px under the top edge and 10px from the left one; on Desktop it
        // sits on the left side over the trees list
        // `--tree-popup-seam`: the tree popup right edge - the map's left edge on Desktop, 
        // its own edge on Mobile - where the close button is partly displayed out of the screen (see map.less)
        style={{
          // `centered` puts the window in the middle of the screen
          top: emptyCentered ? 0 : `${POPUP_TOP_PADDING}px`,
          // antd gives the window an 8px margin of its own under 768px wide, which would add to `top`
          marginTop: 0,
          // Flush with the trees list on Desktop, `POPUP_MOBILE_LEFT_PADDING` from the screen edge on Mobile,
          // centred on Mobile landscape
          marginLeft: emptyCentered
            ? undefined
            : windowWidth
              ? `${POPUP_DESKTOP_LEFT_PADDING}px`
              : centered ? 'auto' : `${POPUP_MOBILE_LEFT_PADDING}px`,
          marginRight: 'auto',
          // Mobile only: antd's own cap (`100% - 32px` from main.less, `100vw - 16px` under 768px) would
          // leave more than the half disc on the right
          ...(windowWidth
            ? {}
            : { maxWidth: `calc(100vw - ${POPUP_MOBILE_LEFT_PADDING + POPUP_MOBILE_RIGHT_PADDING}px)` }),
          ...(popupSeam !== undefined ? { ['--tree-popup-seam' as string]: `${Math.round(popupSeam)}px` } : {}),
        } as React.CSSProperties}
        onCancel={initiateClose}
        // No footer: the window's own close button on the seam is the way out, so the "Close" row under
        // the actions was a second one for the same thing
        footer={null}
        bodyStyle={{ touchAction: 'auto' }}
        // The popup is torn down (its root unmounted by `TreeMaps`) once the close animation has
        // played. It used to be torn down by an effect as soon as `open` became false, which
        // removed the modal from the DOM before `ant-zoom-leave` could start: the window vanished
        // instead of collapsing - and there was nothing left to direct the collapse at.
        afterClose={onClose}
      >
        {/* Any node inside the window is enough for the collapse (`collapseModalAtPoint` looks the
            panel up from it). Hidden, so it takes no room in the modal body. */}
        <span ref={collapseAnchorRef} style={{ display: 'none' }} />
        <MapPopupErrorBoundary>
          {pageLoading
            ? (
              <div>
                <div>
                  Loading...
                </div>
                <div>
                  <Empty description={
                    <span>
                      Loading data ... <Spin />
                    </span>
                  } image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              </div>
            )
            : tree ? (
              <>
                <Row gutter={desktopLayout ? POPUP_DESKTOP_GUTTER : [0, POPUP_SECTION_GAP]} align="top">
                  {/* Desktop (and a tablet wide enough): trees list on left-side, with map on right-side
                      Mobile: only the map full width */}
                  {!desktopLayout ? (
                    <>
                      <Col span={24}>
                        {paginationBlock}
                        {photoBlock}
                      </Col>
                      <Col span={24}>{descriptionBlock}</Col>
                    </>
                  ) : (
                    <>
                      {/* `minWidth: 0`: a long value (the planting hash) must not widen this column and
                          push the photo onto a second row */}
                      <Col flex={`0 0 ${POPUP_DESKTOP_COLUMNS[0]}`} style={{ minWidth: 0 }}>
                        {descriptionBlock}
                        {/* The two actions close the details column, under the last field */}
                        <div style={{ marginTop: POPUP_SECTION_GAP }}>{actionsBlock}</div>
                      </Col>
                      <Col flex={`0 0 ${POPUP_DESKTOP_COLUMNS[1]}`} style={{ minWidth: 0 }}>
                        {/* The photo tabs sits over the picture */}
                        {paginationBlock}
                        {photoBlock}
                      </Col>
                    </>
                  )}
                </Row>
                {/* Under the text and the photo, full width: the validations - and on Mobile the twoactions too */}
                {!desktopLayout ? (
                  <div style={{ marginTop: POPUP_SECTION_GAP }}>
                    {validationsBlock}
                    <div style={{ marginTop: validationsBlock ? POPUP_SECTION_GAP : 0 }}>{actionsBlock}</div>
                  </div>
                ) : (
                  validationsBlock && (
                    <div style={{ marginTop: POPUP_SECTION_GAP, paddingTop: POPUP_DESKTOP_VALIDATIONS_PADDING_TOP }}>
                      {validationsBlock}
                    </div>
                  )
                )}
              </>
            ) : (
              <Empty description={'Tree data could not be loaded.'} />
            )
          }
        </MapPopupErrorBoundary>
      </Modal>

      {/* The close button is a page-level element, not part of the window: a `fixed` element inside a window
          that is still zooming in, being re-centred or scrolling is positioned against the window, and the
          button used to travel across the popup before settling. It appears once the content has rendered */}
      {!closing && !pageLoading && createPortal(
        <button
          type="button"
          className="tree-popup-close"
          aria-label="Close"
          title="Close"
          onClick={initiateClose}
          style={popupSeam !== undefined
            ? { ['--tree-popup-seam' as string]: `${Math.round(popupSeam)}px` } as React.CSSProperties
            : undefined}
        >
          <CloseOutlined />
        </button>,
        document.body,
      )}

      {/* Desktop: a portrait photo opens over the window itself, the map staying visible beside it
          (see `leftWidth`) */}
      <PhotoViewer
        url={viewerImageUrl}
        leftWidth={windowWidth ? popupSeam : undefined}
        popupSeam={windowWidth ? undefined : popupSeam}
        onClose={() => setViewerImageUrl(undefined)}
      />
    </>
  );
}

